import {ListItem, Skeleton, Stack, Typography, useTheme} from '@mui/material';
import React, {Fragment} from 'react';

import {IconIllustration} from 'presentation/components';
import Icon from 'presentation/icons';
import {StyledCardContainer, StyledList, StyledListItemIcon, StyledListItemText} from './styles';
import {CardContentProps, CardHeaderProps, StyledCardProps} from './types';

function CardContainer({children, ...cardProps}: StyledCardProps) {
	return (
		<StyledCardContainer elevation={0} {...cardProps}>
			{children}
		</StyledCardContainer>
	);
}

function CardHeader({
	iconProps,
	titleTypographyProps,
	title,
	subtitle,
	titleColor,
	customIcon,
	children,
	hiddenIcon = false,
	gap,
}: CardHeaderProps) {
	const {palette} = useTheme();

	return (
		<Stack gap={gap ?? 2}>
			<Stack justifyContent="space-between" alignItems="center" direction="row">
				<Stack>
					<Typography
						variant="h6"
						color={titleColor || palette.primary.main}
						{...titleTypographyProps}>
						{title}
					</Typography>
					{subtitle}
				</Stack>
				{!hiddenIcon ? (
					<Fragment>
						{!customIcon ? (
							<IconIllustration variant="primary" size="small" {...iconProps} />
						) : (
							customIcon
						)}
					</Fragment>
				) : null}
			</Stack>
			{children}
		</Stack>
	);
}

function CardHeaderSkeleton() {
	return (
		<Stack justifyContent="space-between" alignItems="center" direction="row" gap={2}>
			<Stack flex={1}>
				<Skeleton variant="text" sx={{fontSize: '1.25rem'}} />
				<Skeleton variant="text" sx={{fontSize: '1rem'}} />
			</Stack>
			<Skeleton variant="circular" width={56} height={56} />
		</Stack>
	);
}

function CardContent({list, mb, switchFirstTwoListItemsPosition, ...listProps}: CardContentProps) {
	const displayList =
		switchFirstTwoListItemsPosition && list.length >= 2
			? [list[1], list[0], ...list.slice(2)]
			: list;

	return (
		<StyledList disablePadding {...listProps}>
			{displayList.map((item, index) =>
				item.hide ? null : (
					<ListItem
						key={`contentItem${String(index)}`}
						alignItems={item.alignItems ?? 'flex-start'}
						disablePadding
						sx={item.sx}>
						<StyledListItemIcon>
							{item.customIcon ?? (
								<Icon name={item.iconName} color={item.iconColor} size={20} />
							)}
						</StyledListItemIcon>
						<StyledListItemText
							primary={item.primaryText}
							secondary={item.secondaryText}
							$flexDirection={item.flexDirection}
							$mb={mb}
							$customTypographyGap={item.customTypographyGap}
							primaryTypographyProps={{
								color: item.primaryTextColor,
								fontSize: item.primaryTextFontSize,
								lineHeight: item.primaryTextLineHeight,
							}}
							secondaryTypographyProps={{
								color: item.secondaryTextColor,
								fontSize: item.secondaryTextFontSize,
								lineHeight: item.secondaryTextLineHeight,
							}}
						/>
					</ListItem>
				),
			)}
		</StyledList>
	);
}

function CardFooterSkeleton() {
	return (
		<Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap">
			<Skeleton sx={{borderRadius: '12px'}} variant="rounded" width={100} height={24} />
			<Skeleton sx={{borderRadius: '21px'}} variant="rounded" width={133} height={42} />
		</Stack>
	);
}

export const StyledCard = {
	HeaderSkeleton: CardHeaderSkeleton,
	FooterSkeleton: CardFooterSkeleton,
	Container: CardContainer,
	Content: CardContent,
	Header: CardHeader,
};
